import { storableError } from '../../util/errors';
import { conversations, chatMessages, manualTransactions, sendTwilioMessage } from '../../util/api';
import socket from '../../util/socket';
import { fetchCurrentUser, fetchSmsBalance } from '../../ducks/user.duck';
import { userTypeOf } from '../../util/userType';

// ================ Action types ================ //

export const LOAD_CONVERSATIONS_REQUEST = 'app/InboxPage/LOAD_CONVERSATIONS_REQUEST';
export const LOAD_CONVERSATIONS_SUCCESS = 'app/InboxPage/LOAD_CONVERSATIONS_SUCCESS';
export const LOAD_CONVERSATIONS_ERROR = 'app/InboxPage/LOAD_CONVERSATIONS_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/InboxPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/InboxPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/InboxPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/InboxPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/InboxPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/InboxPage/SEND_MESSAGE_ERROR';

export const MARK_READ_REQUEST = 'app/InboxPage/MARK_READ_REQUEST';
export const MARK_READ_SUCCESS = 'app/InboxPage/MARK_READ_SUCCESS';
export const MARK_READ_ERROR = 'app/InboxPage/MARK_READ_ERROR';

export const SET_PARTICIPANTS = 'app/InboxPage/SET_PARTICIPANTS';
export const UPDATE_CONVERSATION_LOCALLY = 'app/InboxPage/UPDATE_CONVERSATION_LOCALLY';
export const SET_LOADING_STATE = 'app/InboxPage/SET_LOADING_STATE';
export const SET_PROFILE_LISTING_ADMIN_ID = 'app/InboxPage/SET_PROFILE_LISTING_ADMIN_ID';

export const CONVERSATION_READ_UPDATE = 'app/InboxPage/CONVERSATION_READ_UPDATE';


// ================ Reducer ================ //

const initialState = {
  conversations: [],
  messages: {},          // { [conversationId]: Message[] } sorted oldest→newest
  participants: {},      // { [conversationId]: ParticipantProfile }
  messageCursors: {},    // { [conversationId]: string | null } oldest createdAt loaded; null = start reached
  ongoingMessageFetches: [],
  loading: false,
  profileListingAdminId: null,
  sendMessageInProgress: false,
  sendMessageError: null,
  markReadInProgress: false,
  markReadError: null,
};

const dedupeMessages = (existing, incoming) => {
  const seen = new Map(existing.map(m => [m.messageId, m]));
  incoming.forEach(m => { if (m.messageId) seen.set(m.messageId, m); });
  return Array.from(seen.values()).sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case LOAD_CONVERSATIONS_REQUEST:
      return { ...state, loading: true };
    case LOAD_CONVERSATIONS_SUCCESS:
      return { ...state, loading: false, conversations: payload };
    case LOAD_CONVERSATIONS_ERROR:
      return { ...state, loading: false };

    case FETCH_MESSAGES_REQUEST:
      return {
        ...state,
        ongoingMessageFetches: state.ongoingMessageFetches.includes(payload.conversationId)
          ? state.ongoingMessageFetches
          : [...state.ongoingMessageFetches, payload.conversationId],
      };
    case FETCH_MESSAGES_SUCCESS: {
      const { conversationId, messages, oldestCreatedAt, hasMore } = payload;
      const existing = state.messages[conversationId] || [];
      const merged = dedupeMessages(existing, messages);
      return {
        ...state,
        ongoingMessageFetches: state.ongoingMessageFetches.filter(id => id !== conversationId),
        messages: { ...state.messages, [conversationId]: merged },
        messageCursors: {
          ...state.messageCursors,
          [conversationId]: hasMore ? oldestCreatedAt : null,
        },
      };
    }
    case FETCH_MESSAGES_ERROR:
      return {
        ...state,
        ongoingMessageFetches: state.ongoingMessageFetches.filter(id => id !== payload.conversationId),
      };

    case SEND_MESSAGE_REQUEST:
      return { ...state, sendMessageInProgress: true, sendMessageError: null };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case MARK_READ_REQUEST:
      return { ...state, markReadInProgress: true, markReadError: null };
    case MARK_READ_SUCCESS: {
      const { conversationId, userId } = payload;
      const updatedConversations = state.conversations.map(c => {
        if (c.conversationId !== conversationId) return c;
        return {
          ...c,
          participants: c.participants.map(p =>
            p.userId === userId ? { ...p, unreadCount: 0, lastReadAt: new Date().toISOString() } : p
          ),
        };
      });
      return { ...state, markReadInProgress: false, conversations: updatedConversations };
    }
    case MARK_READ_ERROR:
      return { ...state, markReadInProgress: false, markReadError: payload };

    case CONVERSATION_READ_UPDATE: {
      const { conversationId, userId, readAt } = payload;
      const updatedConversations = state.conversations.map(c => {
        if (c.conversationId !== conversationId) return c;
        return {
          ...c,
          participants: c.participants.map(p =>
            p.userId === userId ? { ...p, unreadCount: 0, lastReadAt: readAt || new Date().toISOString() } : p
          ),
        };
      });
      return { ...state, conversations: updatedConversations };
    }

    case SET_PARTICIPANTS:
      return { ...state, participants: { ...state.participants, ...(payload || {}) } };

    case UPDATE_CONVERSATION_LOCALLY: {
      const { conversationId, patch } = payload;
      const updatedConversations = state.conversations.map(c =>
        c.conversationId === conversationId ? { ...c, ...patch } : c
      );
      // Move updated conversation to top (it has newest message)
      const idx = updatedConversations.findIndex(c => c.conversationId === conversationId);
      if (idx > 0) {
        const [moved] = updatedConversations.splice(idx, 1);
        updatedConversations.unshift(moved);
      }
      return { ...state, conversations: updatedConversations };
    }

    case SET_LOADING_STATE:
      return { ...state, loading: payload };

    case SET_PROFILE_LISTING_ADMIN_ID:
      return { ...state, profileListingAdminId: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const loadConversationsRequest = () => ({ type: LOAD_CONVERSATIONS_REQUEST });
const loadConversationsSuccess = payload => ({ type: LOAD_CONVERSATIONS_SUCCESS, payload });
const loadConversationsError = e => ({ type: LOAD_CONVERSATIONS_ERROR, payload: e });

const fetchMessagesRequest = conversationId => ({ type: FETCH_MESSAGES_REQUEST, payload: { conversationId } });
const fetchMessagesSuccess = (conversationId, messages, oldestCreatedAt, hasMore) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { conversationId, messages, oldestCreatedAt, hasMore },
});
const fetchMessagesError = (conversationId, e) => ({
  type: FETCH_MESSAGES_ERROR,
  payload: { conversationId, ...storableError(e) },
});

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, payload: e });

const markReadRequest = () => ({ type: MARK_READ_REQUEST });
const markReadSuccess = (conversationId, userId) => ({ type: MARK_READ_SUCCESS, payload: { conversationId, userId } });
const markReadError = e => ({ type: MARK_READ_ERROR, payload: e });

export const conversationReadUpdate = (conversationId, userId, readAt) => ({
  type: CONVERSATION_READ_UPDATE,
  payload: { conversationId, userId, readAt },
});

export const setParticipants = participantsByConvId => ({ type: SET_PARTICIPANTS, payload: participantsByConvId });
export const updateConversationLocally = (conversationId, patch) => ({
  type: UPDATE_CONVERSATION_LOCALLY,
  payload: { conversationId, patch },
});
export const setLoadingState = loading => ({ type: SET_LOADING_STATE, payload: loading });
const setProfileListingAdminId = id => ({ type: SET_PROFILE_LISTING_ADMIN_ID, payload: id });


// ================ Helpers ================ //

const PARTICIPANT_FETCH_DELAY_MS = 120;
const waitFor = ms => new Promise(resolve => setTimeout(resolve, ms));

const buildParticipantProfile = (user, profileImage, fallbackProfileImage) => {
  const img = profileImage || fallbackProfileImage || null;
  return {
    displayName: user?.attributes?.profile?.displayName || '',
    id: user?.id?.uuid,
    bio: user?.attributes?.profile?.bio || '',
    agencyOrCustomerName:
      user?.attributes?.profile?.publicData?.agencyProfile?.name ||
      user?.attributes?.profile?.publicData?.customerProfile?.name || '',
    agencyDescription: user?.attributes?.profile?.publicData?.agencyProfile?.agencyDescription || '',
    customerDescription: user?.attributes?.profile?.publicData?.customerProfile?.customerDescription || '',
    userType: userTypeOf(user) || '',
    profileImage: img,
    profileImageId: img?.id?.uuid || img?.id || null,
    profileListingId:
      user?.attributes?.profile?.publicData?.firstListingId ||
      user?.attributes?.profile?.publicData?.profileListingId || null,
    phoneNumber: user?.attributes?.profile?.publicData?.phoneNumber || null,
  };
};

// Preload participant profiles for all conversations sequentially to avoid rate limits
const preloadParticipants = (convList, currentUserAdminId) => async (dispatch, getState, sdk) => {
  const userCache = new Map();

  for (let i = 0; i < convList.length; i++) {
    const conv = convList[i];
    const convId = conv.conversationId;
    const otherUserId = conv.participantIds.find(id => id !== currentUserAdminId);
    if (!otherUserId) continue;

    const existing = getState()?.InboxPage?.participants?.[convId];
    if (existing?.id && (existing?.displayName || existing?.agencyOrCustomerName)) {
      if (i < convList.length - 1) await waitFor(PARTICIPANT_FETCH_DELAY_MS);
      continue;
    }

    let cached = userCache.get(otherUserId);
    if (!cached) {
      try {
        const userResponse = await sdk.users.show({
          id: otherUserId,
          include: ['profileImage'],
          'fields.image': ['variants.default', 'variants.square-small', 'variants.square-small2x'],
        });
        const userData = userResponse?.data?.data || null;
        const included = Array.isArray(userResponse?.data?.included) ? userResponse.data.included : [];
        const profileImage = included.find(e => e?.type === 'image') || null;
        cached = { user: userData, profileImage };
      } catch {
        cached = { user: null, profileImage: null };
      }
      userCache.set(otherUserId, cached);
    }

    const profile = cached.user
      ? buildParticipantProfile(cached.user, cached.profileImage, null)
      : { id: otherUserId, displayName: '', agencyOrCustomerName: '', bio: '', agencyDescription: '', customerDescription: '', userType: '', profileImage: null, profileImageId: null, profileListingId: null, phoneNumber: null };

    dispatch(setParticipants({ [convId]: profile }));

    if (i < convList.length - 1) await waitFor(PARTICIPANT_FETCH_DELAY_MS);
  }
};

// ================ Thunks ================ //

export const loadData = (params) => async (dispatch, getState, sdk) => {
  dispatch(loadConversationsRequest());

  const currentUser = getState().user?.currentUser;
  const profileListingId =
    currentUser?.attributes?.profile?.publicData?.profileListingId ||
    currentUser?.attributes?.profile?.publicData?.firstListingId;

  let currentUserAdminId = null;
  try {
    const listingResponse = await sdk.listings.show({ id: profileListingId });
    currentUserAdminId = listingResponse?.data?.data?.attributes?.publicData?.adminId || currentUser?.id?.uuid;
  } catch {
    currentUserAdminId = currentUser?.id?.uuid;
  }

  dispatch(setProfileListingAdminId(currentUserAdminId));

  try {
    const convList = await conversations({ actionType: 'list', userId: currentUserAdminId });
    const deduped = Array.isArray(convList) ? convList : [];

    // Join socket rooms
    deduped.forEach(conv => {
      if (conv.conversationId) socket.emit('join-conversation', conv.conversationId);
    });

    dispatch(loadConversationsSuccess(deduped));

    // Load participant profiles in background
    dispatch(preloadParticipants(deduped, currentUserAdminId));

    return { data: deduped };
  } catch (e) {
    dispatch(loadConversationsError(storableError(e)));
    throw e;
  }
};

export const refreshConversations = () => async (dispatch, getState) => {
  const state = getState();
  const adminId = state.InboxPage?.profileListingAdminId || state.user?.currentUser?.id?.uuid;
  if (!adminId) return;
  try {
    const convList = await conversations({ actionType: 'list', userId: adminId });
    if (Array.isArray(convList)) dispatch(loadConversationsSuccess(convList));
  } catch { /* silent */ }
};

export const fetchMessagesForConversation = (conversationId, options = {}) => async (dispatch, getState, sdk) => {
  if (!conversationId) return false;

  const inboxState = getState()?.InboxPage || {};

  if (inboxState.ongoingMessageFetches.includes(conversationId)) return true;

  const forceRefresh = options.force === true;
  const beforeCursor = options.before || null;

  // Skip if not forced and we already have messages and no cursor (loading older)
  if (!forceRefresh && !beforeCursor) {
    const existing = inboxState.messages?.[conversationId];
    if (Array.isArray(existing) && existing.length > 0) return true;
  }

  dispatch(fetchMessagesRequest(conversationId));

  try {
    const body = { actionType: 'fetch', conversationId };
    if (beforeCursor) body.beforeCreatedAt = beforeCursor;

    const msgs = await chatMessages(body);
    const fetchedMsgs = Array.isArray(msgs) ? msgs : [];

    const oldestCreatedAt = fetchedMsgs.length > 0 ? fetchedMsgs[0].createdAt : null;
    // hasMore: if we got a full page (30), there are probably older messages
    const hasMore = fetchedMsgs.length >= 30;

    dispatch(fetchMessagesSuccess(conversationId, fetchedMsgs, oldestCreatedAt, hasMore));

    // Also refresh participant if we don't have it yet
    if (!inboxState.participants?.[conversationId]) {
      const convList = inboxState.conversations || [];
      const conv = convList.find(c => c.conversationId === conversationId);
      if (conv) dispatch(preloadParticipants([conv], inboxState.profileListingAdminId));
    }

    return true;
  } catch (e) {
    dispatch(fetchMessagesError(conversationId, e));
    return false;
  }
};

export const sendMessage = (conversationId, text, blobName, blobUrl, config, sendSMS) => async (dispatch, getState, sdk) => {
  const state = getState();
  const profileListingAdminId = state.InboxPage.profileListingAdminId;

  const convList = state.InboxPage.conversations || [];
  const conv = convList.find(c => c.conversationId === conversationId);
  const receiverId = conv?.participantIds?.find(id => id !== profileListingAdminId);

  if (!receiverId) return { ok: false, sms: null };

  // Build attachments array from blob info
  const attachments = blobUrl
    ? [{ fileName: blobName || '', fileUrl: blobUrl, fileType: '', fileSize: 0 }]
    : [];

  dispatch(sendMessageRequest());

  let messageId;
  try {
    const saved = await chatMessages({
      actionType: 'send',
      conversationId,
      senderId: profileListingAdminId,
      receiverId,
      text: text || '',
      attachments,
    });
    messageId = saved?.messageId;

    dispatch(sendMessageSuccess());

    // Optimistic: move conversation to top with updated lastMessage
    dispatch(updateConversationLocally(conversationId, {
      lastMessage: {
        senderId: profileListingAdminId,
        type: attachments.length ? 'file' : 'text',
        textPreview: (text || '').slice(0, 100),
        createdAt: new Date().toISOString(),
      },
      lastMessageAt: new Date().toISOString(),
    }));
  } catch (e) {
    dispatch(sendMessageError(storableError(e)));
    return { ok: false, sms: null };
  }

  if (!sendSMS) return { ok: true, sms: null };

  // SMS notification for the saved message. The server finds the other participant, their phone
  // and opt-in, words the text and pays for it from the sender's credit, so only the message is
  // named here. A failed SMS request never undoes the chat message.
  let sms;
  try {
    sms = await sendTwilioMessage({ conversationId, messageId });
  } catch (e) {
    sms = { sms: 'skipped', reason: 'unavailable' };
  }
  // The server may have spent a credit; refresh what the page shows.
  dispatch(fetchSmsBalance());
  return { ok: true, sms };
};

export const markRead = (conversationId) => async (dispatch, getState) => {
  dispatch(markReadRequest());

  const userId = getState()?.InboxPage?.profileListingAdminId;
  if (!userId) {
    dispatch(markReadError(new Error('No profileListingAdminId')));
    return;
  }

  try {
    await conversations({ actionType: 'markRead', conversationId, userId });
    dispatch(markReadSuccess(conversationId, userId));
    dispatch(fetchCurrentUser({ updateNotifications: true }));
  } catch (e) {
    dispatch(markReadError(storableError(e)));
  }
};

export const getOrCreateConversation = (senderId, receiverId) => async () => {
  const result = await conversations({ actionType: 'getOrCreate', senderId, receiverId });
  return result;
};
